import React from 'react'
import {
  Avatar,
  Badge,
  Box,
  Chip,
  IconButton,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import EmailIcon from '@mui/icons-material/EmailRounded'
import SendIcon from '@mui/icons-material/SendRounded'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import ChatIcon from '@mui/icons-material/ChatRounded'
import RadioIcon from '@mui/icons-material/RadioRounded'
import FlagIcon from '@mui/icons-material/FlagRounded'
import type { ThreadSummary } from '../../api/threads'
import useStationStore from '../../store/station'
import { formatMessageTimestamp, resolveEpochMs } from '../../utils/time'
import useUserSettingsStore, { clampChatListPreviewLines } from '../../store/userSettings'
import {
  hasFlagged,
  hasOnAir,
  latestFlaggedMessageId,
  latestOnAirMessageId,
} from '../../utils/inbox/threadMarks'
import { ensureCallable } from '../../utils/inbox/safety'

const platformIcons: Record<string, React.ReactElement> = {
  email: <EmailIcon fontSize="small" />,
  telegram: <SendIcon fontSize="small" />,
  whatsapp: <WhatsAppIcon fontSize="small" />,
  messenger: <ChatIcon fontSize="small" />,
}

const formatContent = (content: string): string => {
  if (!content) {
    return '(no content)'
  }

  const normalized = content.replace(/\s+/g, ' ').trim()

  if (normalized.length <= 140) {
    return normalized
  }

  return `${normalized.slice(0, 137)}…`
}

interface ThreadRowProps {
  thread: ThreadSummary
  selected?: boolean
  onClick?: () => void
  onKeyDown?: React.KeyboardEventHandler<HTMLDivElement>
  onJumpToOnAir?: (thread: ThreadSummary) => void
  onJumpToFlagged?: (thread: ThreadSummary) => void
}

const ThreadRow: React.FC<ThreadRowProps> = ({
  thread,
  selected = false,
  onClick,
  onKeyDown,
  onJumpToOnAir,
  onJumpToFlagged,
}) => {
  const displayName = thread.contact_name ? thread.contact_name.toUpperCase() : 'UNKNOWN'
  const latestContent = formatContent(thread.latest_content ?? '')
  const stationTimezone = useStationStore((state) => state.activeStation?.timezone)
  const latestEpochMs = React.useMemo(
    () =>
      resolveEpochMs({
        epochSeconds: thread.latest_received_at_epoch ?? undefined,
        isoString: thread.latest_received_at_iso_utc ?? thread.latest_received_at ?? null,
      }),
    [thread.latest_received_at, thread.latest_received_at_epoch, thread.latest_received_at_iso_utc],
  )
  const timestamp = formatMessageTimestamp(latestEpochMs, stationTimezone)
  const icon = platformIcons[thread.latest_platform] ?? <ChatIcon fontSize="small" />
  const unreadCount = thread.unread_count ?? 0
  const showOnAir = hasOnAir(thread)
  const showFlagged = hasFlagged(thread)
  const latestOnAirId = latestOnAirMessageId(thread)
  const latestFlaggedId = latestFlaggedMessageId(thread)
  const previewLinesSetting = useUserSettingsStore((state) => state.chatListPreviewLines)
  const previewLines = clampChatListPreviewLines(previewLinesSetting)
  const showPreview = previewLines > 0
  const isAutoresponder = Boolean(thread.latest_is_autoresponder ?? thread.latestIsAutoresponder)
  const autoRuleName = thread.latest_auto_rule_name ?? thread.latestAutoRuleName ?? null

  const autoMarker = React.useMemo(() => {
    if (!isAutoresponder) {
      return null
    }

    const ruleName = typeof autoRuleName === 'string' && autoRuleName.trim().length > 0 ? autoRuleName.trim() : ''
    const chip = (
      <Chip label="Auto" size="small" color="info" variant="outlined" sx={{ height: 20, fontSize: 10 }} />
    )

    if (!ruleName) {
      return chip
    }

    return (
      <Tooltip title={`Autoresponder: ${ruleName}`} placement="top">
        <span>{chip}</span>
      </Tooltip>
    )
  }, [autoRuleName, isAutoresponder])

  const handleJumpOnAir = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation()
      event.preventDefault()
      if (!ensureCallable(onJumpToOnAir, 'onJumpToOnAir', { threadId: thread.contact_id })) {
        return
      }
      onJumpToOnAir(thread)
    },
    [onJumpToOnAir, thread],
  )

  const handleJumpFlagged = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation()
      event.preventDefault()
      if (!ensureCallable(onJumpToFlagged, 'onJumpToFlagged', { threadId: thread.contact_id })) {
        return
      }
      onJumpToFlagged(thread)
    },
    [onJumpToFlagged, thread],
  )

  return (
    <ListItem disablePadding selected={selected} className="rpa-list-row">
      <ListItemButton
        role="button"
        onClick={onClick}
        onKeyDown={onKeyDown}
        selected={selected}
        data-thread-id={thread.contact_id}
        sx={{
          alignItems: 'flex-start',
          py: 0.9,
          px: 1.25,
          gap: 1,
          borderRadius: 1.25,
          backgroundColor: selected ? 'action.selected' : unreadCount > 0 ? 'action.hover' : 'transparent',
        }}
      >
        <ListItemAvatar sx={{ minWidth: 48 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 0.5,
              minWidth: 40,
            }}
          >
            <Avatar
              variant="rounded"
              sx={{ bgcolor: (theme) => theme.palette.primary.light, width: 36, height: 36 }}
            >
              {icon}
            </Avatar>
            {(showOnAir || showFlagged) && (
              <Stack direction="row" spacing={0.25} alignItems="center" sx={{ lineHeight: 1 }}>
                {showOnAir ? (
                  <Tooltip title="Jump to on-air message">
                    <span>
                      <IconButton
                        size="small"
                        color={showOnAir ? 'secondary' : 'default'}
                        onClick={handleJumpOnAir}
                        disabled={!onJumpToOnAir || !latestOnAirId}
                        aria-label="Jump to on-air message"
                      >
                        <RadioIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                ) : null}
                {showFlagged ? (
                  <Tooltip title="Jump to flagged message">
                    <span>
                      <IconButton
                        size="small"
                        onClick={handleJumpFlagged}
                        disabled={!onJumpToFlagged || !latestFlaggedId}
                        aria-label="Jump to flagged message"
                        color={showFlagged ? 'warning' : 'default'}
                      >
                        <FlagIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                ) : null}
              </Stack>
            )}
          </Box>
        </ListItemAvatar>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
            <Box sx={{ minWidth: 0 }}>
              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: unreadCount > 0 ? 700 : 600,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    lineHeight: 1.2,
                    flexGrow: 1,
                  }}
                >
                  {displayName}
                </Typography>
                {autoMarker ? <Box sx={{ flexShrink: 0 }}>{autoMarker}</Box> : null}
              </Stack>
              {showPreview ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  className="ThreadRow__preview"
                  sx={{ mt: 0.25, lineHeight: 1.35, '--clamp-lines': previewLines } as const}
                >
                  {latestContent}
                </Typography>
              ) : null}
            </Box>
            <Stack spacing={0.5} alignItems="flex-end">
              <Typography variant="caption" color="text.secondary" className="rpa-list-row-meta">
                {timestamp}
              </Typography>
              {unreadCount > 0 ? (
                <Badge
                  color="primary"
                  badgeContent={unreadCount}
                  sx={{ '& .MuiBadge-badge': { fontWeight: 600, minWidth: 20 } }}
                />
              ) : null}
            </Stack>
          </Stack>
        </Box>
      </ListItemButton>
    </ListItem>
  )
}

export default ThreadRow
